import { useMemo } from 'react';

import { InvestorDetailsLegalEntityAssetClassTargetsQuery } from '@/__generated__/graphql';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import {
  overCommitmentRatioModeSummaryTableColumns,
  pacingModelModeSimplifiedSummaryTableColumns,
  pacingModelModeSummaryTableColumns
} from '../../constants';

interface ISum {
  total: string;
  target: number;
  targetAllocations: number;
  exposure: number;
  capacity: number;
  currentNAV: number;
  unfundedCommitments: number;
}

export const calculateCapacity = (
  row: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number],
  commitmentPlansTotal: number | null,
  targetOCRatio: number | null,
  targetSubAssetClasses: boolean
) => {
  if (commitmentPlansTotal !== null && targetOCRatio === null) {
    if (commitmentPlansTotal > 0) {
      if (targetSubAssetClasses && 'subAssetClasses' in row) {
        return row.subAssetClasses.reduce((sum, subAssetClass) => {
          return sum + (subAssetClass.illiquid ? subAssetClass.pacingTarget?.remainingPacingTarget || 0 : subAssetClass.capacity);
        }, 0);
      }
      return row.illiquid ? row.pacingTarget?.remainingPacingTarget || 0 : row.capacity;
    } else if (commitmentPlansTotal === 0) {
      return !row.illiquid ? row.capacity : 0;
    }
  }
  return row.capacity;
};

interface SummaryTableSumFooterProps {
  investorSummaryRows: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'];
  ignoreAllocationTargets: boolean;
  commitmentPlansTotal: number | null;
  targetOCRatio: number | null;
  targetSubAssetClasses: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
}

const SummaryTableSumFooter = ({
  investorSummaryRows,
  ignoreAllocationTargets,
  commitmentPlansTotal,
  targetOCRatio,
  targetSubAssetClasses,
  entityType
}: SummaryTableSumFooterProps) => {
  const rowSum: ISum = useMemo(() => {
    const sum = {
      total: 'Total',
      target: 0,
      targetAllocations: 0,
      exposure: 0,
      capacity: 0,
      currentNAV: 0,
      unfundedCommitments: 0
    };

    investorSummaryRows.forEach((row) => {
      sum.target += Number((row.percentage || 0).toFixed(2));
      sum.targetAllocations += row.targetAllocation;
      sum.exposure += row.economicExposure;
      sum.currentNAV += row.currentNAV || 0;
      sum.unfundedCommitments += row.unfundedCommitments || 0;
      sum.capacity += calculateCapacity(row, commitmentPlansTotal, targetOCRatio, targetSubAssetClasses);
    });

    return sum;
  }, [investorSummaryRows, commitmentPlansTotal, targetOCRatio]);

  // if (ignoreAllocationTargets && targetOCRatio !== null) {
  //   return (
  //     <TableSumWrap>
  //       <TableSumItem width={overCommitmentRatioModeSimplifiedSummaryTableColumns[0]?.width}>{rowSum.total}</TableSumItem>
  //       <TableSumItem width={overCommitmentRatioModeSimplifiedSummaryTableColumns[1]?.width}>
  //         <FormattingTooltip>{rowSum.exposure}</FormattingTooltip>
  //       </TableSumItem>
  //     </TableSumWrap>
  //   );
  // }

  if (ignoreAllocationTargets || entityType === InvestorDetailsEntityTypeEnum.GroupedEntity) {
    return (
      <TableSumWrap>
        <TableSumItem width={pacingModelModeSimplifiedSummaryTableColumns[0]?.width}>{rowSum.total}</TableSumItem>
        <TableSumItem width={pacingModelModeSimplifiedSummaryTableColumns[1]?.width}>
          <FormattingTooltip>{rowSum.currentNAV}</FormattingTooltip>
        </TableSumItem>
        <TableSumItem width={pacingModelModeSimplifiedSummaryTableColumns[2]?.width}>
          <FormattingTooltip>{rowSum.unfundedCommitments}</FormattingTooltip>
        </TableSumItem>
      </TableSumWrap>
    );
  }

  if (targetOCRatio === null) {
    return (
      <TableSumWrap>
        <TableSumItem width={pacingModelModeSummaryTableColumns[0].width}>{rowSum.total}</TableSumItem>
        <TableSumItem width={pacingModelModeSummaryTableColumns[1].width}>
          <Tooltip title={`${rowSum.target.toFixed(2)}%`}>{rowSum.target.toFixed(2)}%</Tooltip>
        </TableSumItem>
        <TableSumItem width={pacingModelModeSummaryTableColumns[2].width}>
          <FormattingTooltip>{rowSum.targetAllocations}</FormattingTooltip>
        </TableSumItem>
        <TableSumItem width={pacingModelModeSummaryTableColumns[3].width}>
          <FormattingTooltip>{rowSum.currentNAV}</FormattingTooltip>
        </TableSumItem>
        <TableSumItem width={pacingModelModeSummaryTableColumns[4].width}>
          <FormattingTooltip>{rowSum.unfundedCommitments}</FormattingTooltip>
        </TableSumItem>
        <TableSumItem width={pacingModelModeSummaryTableColumns[5].width}>
          {commitmentPlansTotal === 0 ? <>--</> : <FormattingTooltip>{rowSum.capacity}</FormattingTooltip>}
        </TableSumItem>
      </TableSumWrap>
    );
  }

  return (
    <TableSumWrap>
      <TableSumItem width={overCommitmentRatioModeSummaryTableColumns[0].width}>{rowSum.total}</TableSumItem>
      <TableSumItem width={overCommitmentRatioModeSummaryTableColumns[1].width}>
        <Tooltip title={`${rowSum.target.toFixed(2)}%`}>{rowSum.target.toFixed(2)}%</Tooltip>
      </TableSumItem>
      <TableSumItem width={overCommitmentRatioModeSummaryTableColumns[2].width}>
        <FormattingTooltip>{rowSum.targetAllocations}</FormattingTooltip>
      </TableSumItem>
      <TableSumItem width={overCommitmentRatioModeSummaryTableColumns[3].width}>
        <FormattingTooltip>{rowSum.exposure}</FormattingTooltip>
      </TableSumItem>
      <TableSumItem width={overCommitmentRatioModeSummaryTableColumns[4].width}>
        <FormattingTooltip>{rowSum.capacity}</FormattingTooltip>
      </TableSumItem>
    </TableSumWrap>
  );
};

export default SummaryTableSumFooter;

const TableSumWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  width: 100%;
  min-height: 60px;
  background-color: ${themeVar('layer.2')};
  border-radius: 8px;
  padding: 0 24px;
`;

const TableSumItem = styled.div<{ width: number }>`
  display: flex;
  justify-content: flex-start;
  width: ${({ width }) => `${width}%`};
  color: ${themeVar('font.base')};
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
`;
